import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { proxiedImage } from '../utils/imageProxy';
import SmartImage from './SmartImage';

const PropertyGallery = ({ images = [], alt = '' }) => {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const touchStartX = useRef(null);

  if (!images.length) return null;

  const prev = () => setActive((i) => (i - 1 + images.length) % images.length);
  const next = () => setActive((i) => (i + 1) % images.length);

  const onTouchStart = (e) => { touchStartX.current = e.changedTouches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > 50) prev();
    else if (dx < -50) next();
    touchStartX.current = null;
  };

  return (
    <div style={styles.wrapper} data-testid="property-gallery">
      <div
        style={styles.mainBox}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <SmartImage
          src={images[active]}
          alt={`${alt} - ${active + 1}/${images.length}`}
          width={1200}
          style={{ ...styles.mainImage, cursor: 'zoom-in' }}
          imgStyle={{ height: '100%' }}
          onClick={() => setLightbox(true)}
          testId="property-main-image"
          priority={active === 0}
        />
        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              style={{ ...styles.navBtn, left: '1rem' }}
              data-testid="gallery-prev"
              aria-label="Vorige"
            >
              <ChevronLeft size={28} />
            </button>
            <button
              type="button"
              onClick={next}
              style={{ ...styles.navBtn, right: '1rem' }}
              data-testid="gallery-next"
              aria-label="Volgende"
            >
              <ChevronRight size={28} />
            </button>
            <span style={styles.counter}>{active + 1} / {images.length}</span>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div style={styles.thumbs} data-testid="gallery-thumbnails">
          {images.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              style={{ ...styles.thumbBtn, ...(i === active ? styles.thumbActive : {}) }}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`Foto ${i + 1}`}
            >
              <img src={proxiedImage(src, 200)} alt="" style={styles.thumbImg} loading="lazy" decoding="async" fetchPriority="low" />
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          style={styles.lightbox}
          onClick={() => setLightbox(false)}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(false)}
            style={styles.closeBtn}
            aria-label="Sluiten"
          >
            <X size={28} />
          </button>
          <img src={proxiedImage(images[active], 1600)} alt={alt} style={styles.lightboxImg} />
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                style={{ ...styles.navBtn, left: '1rem', color: 'white' }}
              >
                <ChevronLeft size={32} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                style={{ ...styles.navBtn, right: '1rem', color: 'white' }}
              >
                <ChevronRight size={32} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  wrapper: { width: '100%' },
  mainBox: {
    position: 'relative',
    width: '100%',
    backgroundColor: '#1A1918',
    overflow: 'hidden',
  },
  mainImage: {
    width: '100%',
    height: 'clamp(280px, 55vw, 600px)',
    objectFit: 'cover',
    display: 'block',
    cursor: 'zoom-in',
  },
  navBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(255,255,255,0.85)',
    border: 'none',
    color: '#1A1918',
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 2,
  },
  counter: {
    position: 'absolute',
    bottom: '1rem',
    right: '1rem',
    backgroundColor: 'rgba(26,25,24,0.75)',
    color: 'white',
    padding: '0.4rem 0.85rem',
    fontSize: '0.8rem',
    letterSpacing: '0.05em',
  },
  thumbs: {
    display: 'flex',
    gap: '0.5rem',
    padding: '0.75rem 0',
    overflowX: 'auto',
    scrollbarWidth: 'thin',
  },
  thumbBtn: {
    flex: '0 0 90px',
    height: '70px',
    border: '2px solid transparent',
    backgroundColor: 'transparent',
    padding: 0,
    cursor: 'pointer',
    overflow: 'hidden',
  },
  thumbActive: { borderColor: '#C5A059' },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  lightbox: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.92)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
  },
  lightboxImg: {
    maxWidth: '95vw',
    maxHeight: '95vh',
    objectFit: 'contain',
  },
  closeBtn: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: 'white',
    border: 'none',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 2001,
  },
};

export default PropertyGallery;
